import React, { useState } from 'react';
import {
  Search,
  ChevronDown,
  ChevronUp,
  Edit,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';

type ViewMode = 'students' | 'courses';

interface CourseGrade {
  courseId: string;
  courseName: string;
  teacherName: string;
  homeworkGrade: number;
  examGrade: number;
  finalGrade: number;
  submissionProgress: { completed: number; total: number };
}

interface StudentGrades {
  id: string;
  name: string;
  avatar?: string;
  yearLevel: string;
  totalCourses: number;
  overallAttendance: number;
  averageGrade: number;
  courses: CourseGrade[];
}

interface GradeCourse {
  id: string;
  name: string;
  cohort: string;
  type: 'sync' | 'async';
  teacher: { id: string; name: string };
  studentCount: number;
  averageGrade: number;
  attendanceSummary: number;
  students: {
    id: string;
    name: string;
    homeworkGrade: number;
    examGrade: number;
    finalGrade: number;
  }[];
}

const mockStudentGrades: StudentGrades[] = [
  {
    id: 's1',
    name: 'Yossi Levy',
    yearLevel: 'Grade 10',
    totalCourses: 5,
    overallAttendance: 92,
    averageGrade: 85,
    courses: [
      { courseId: 'c1', courseName: 'Mathematics Advanced', teacherName: 'Sarah Cohen', homeworkGrade: 88, examGrade: 82, finalGrade: 85, submissionProgress: { completed: 12, total: 14 } },
      { courseId: 'c2', courseName: 'Physics Fundamentals', teacherName: 'David Levi', homeworkGrade: 90, examGrade: 85, finalGrade: 87, submissionProgress: { completed: 10, total: 12 } },
      { courseId: 'c3', courseName: 'English Literature', teacherName: 'Rachel Green', homeworkGrade: 78, examGrade: 80, finalGrade: 79, submissionProgress: { completed: 8, total: 10 } },
    ],
  },
  {
    id: 's2',
    name: 'Maya Cohen',
    yearLevel: 'Grade 10',
    totalCourses: 4,
    overallAttendance: 88,
    averageGrade: 91,
    courses: [
      { courseId: 'c1', courseName: 'Mathematics Advanced', teacherName: 'Sarah Cohen', homeworkGrade: 95, examGrade: 90, finalGrade: 92, submissionProgress: { completed: 14, total: 14 } },
      { courseId: 'c2', courseName: 'Physics Fundamentals', teacherName: 'David Levi', homeworkGrade: 88, examGrade: 92, finalGrade: 90, submissionProgress: { completed: 12, total: 12 } },
    ],
  },
  {
    id: 's3',
    name: 'Daniel Green',
    yearLevel: 'Grade 11',
    totalCourses: 6,
    overallAttendance: 75,
    averageGrade: 72,
    courses: [
      { courseId: 'c1', courseName: 'Mathematics Advanced', teacherName: 'Sarah Cohen', homeworkGrade: 70, examGrade: 68, finalGrade: 69, submissionProgress: { completed: 8, total: 14 } },
    ],
  },
  {
    id: 's4',
    name: 'Noa Shapiro',
    yearLevel: 'Grade 10',
    totalCourses: 5,
    overallAttendance: 100,
    averageGrade: 96,
    courses: [
      { courseId: 'c1', courseName: 'Mathematics Advanced', teacherName: 'Sarah Cohen', homeworkGrade: 98, examGrade: 95, finalGrade: 96, submissionProgress: { completed: 14, total: 14 } },
    ],
  },
];

const mockCourseGrades: GradeCourse[] = [
  {
    id: 'c1',
    name: 'Mathematics Advanced',
    cohort: 'Year 1 / Cohort A2',
    type: 'sync',
    teacher: { id: 't1', name: 'Sarah Cohen' },
    studentCount: 25,
    averageGrade: 82,
    attendanceSummary: 89,
    students: [
      { id: 's1', name: 'Yossi Levy', homeworkGrade: 88, examGrade: 82, finalGrade: 85 },
      { id: 's2', name: 'Maya Cohen', homeworkGrade: 95, examGrade: 90, finalGrade: 92 },
      { id: 's3', name: 'Daniel Green', homeworkGrade: 70, examGrade: 68, finalGrade: 69 },
      { id: 's4', name: 'Noa Shapiro', homeworkGrade: 98, examGrade: 95, finalGrade: 96 },
    ],
  },
  {
    id: 'c2',
    name: 'Physics Fundamentals',
    cohort: 'Year 1 / Cohort B1',
    type: 'sync',
    teacher: { id: 't2', name: 'David Levi' },
    studentCount: 22,
    averageGrade: 78,
    attendanceSummary: 92,
    students: [
      { id: 's1', name: 'Yossi Levy', homeworkGrade: 90, examGrade: 85, finalGrade: 87 },
      { id: 's2', name: 'Maya Cohen', homeworkGrade: 88, examGrade: 92, finalGrade: 90 },
    ],
  },
  {
    id: 'c3',
    name: 'English Literature',
    cohort: 'Year 2 / Cohort A1',
    type: 'async',
    teacher: { id: 't3', name: 'Rachel Green' },
    studentCount: 28,
    averageGrade: 85,
    attendanceSummary: 94,
    students: [],
  },
];

const grades = ['All Grades', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

export function GradesTab() {
  const [viewMode, setViewMode] = useState<ViewMode>('students');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('All Grades');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleToggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getGradeBadgeColor = (grade: number) => {
    if (grade >= 90) return 'bg-green-100 text-green-700';
    if (grade >= 75) return 'bg-yellow-100 text-yellow-700';
    if (grade >= 60) return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-700';
  };

  const getGradeIndicatorColor = (grade: number) => {
    if (grade >= 90) return 'bg-green-500';
    if (grade >= 75) return 'bg-yellow-500';
    if (grade >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div>
      {/* Header with View Mode Toggle */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          {/* Grade Filter */}
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            {grades.map((grade) => (
              <option key={grade} value={grade}>{grade}</option>
            ))}
          </select>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={viewMode === 'students' ? 'Search students...' : 'Search courses...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
            />
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('students')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'students'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            By Students
          </button>
          <button
            onClick={() => setViewMode('courses')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'courses'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            By Courses
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'students' ? (
        <StudentGradesView
          students={mockStudentGrades}
          searchQuery={searchQuery}
          expandedId={expandedId}
          onToggleExpand={handleToggleExpand}
          getGradeBadgeColor={getGradeBadgeColor}
          getGradeIndicatorColor={getGradeIndicatorColor}
        />
      ) : (
        <CourseGradesView
          courses={mockCourseGrades}
          searchQuery={searchQuery}
          expandedId={expandedId}
          onToggleExpand={handleToggleExpand}
          getGradeBadgeColor={getGradeBadgeColor}
          getGradeIndicatorColor={getGradeIndicatorColor}
        />
      )}
    </div>
  );
}

interface StudentGradesViewProps {
  students: StudentGrades[];
  searchQuery: string;
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  getGradeBadgeColor: (grade: number) => string;
  getGradeIndicatorColor: (grade: number) => string;
}

function StudentGradesView({
  students,
  searchQuery,
  expandedId,
  onToggleExpand,
  getGradeBadgeColor,
  getGradeIndicatorColor,
}: StudentGradesViewProps) {
  const filteredStudents = students.filter((student) =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-3">
      {filteredStudents.map((student) => (
        <div
          key={student.id}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          {/* Student Header Row */}
          <div
            className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => onToggleExpand(student.id)}
          >
            {/* Student Avatar */}
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
              {student.name.split(' ').map(n => n[0]).join('')}
            </div>

            {/* Student Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900">{student.name}</h3>
              <p className="text-sm text-gray-500">{student.yearLevel}</p>
            </div>

            {/* Stats */}
            <div className="hidden md:flex items-center gap-6 text-sm">
              <div className="text-gray-600">
                <span className="font-medium">{student.totalCourses}</span> Courses
              </div>
              <div className="text-gray-600">
                <span className="font-medium">{student.overallAttendance}%</span> Attendance
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getGradeBadgeColor(student.averageGrade)}`}>
                Avg: {student.averageGrade}
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Edit grades"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Send message"
              >
                <MessageSquare className="w-4 h-4" />
              </button>

              {/* Expand/Collapse Icon */}
              {expandedId === student.id ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* Expanded Course Grades */}
          {expandedId === student.id && (
            <div className="border-t border-gray-200 bg-gray-50">
              <div className="p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Course Grades</h4>
                <div className="space-y-2">
                  {student.courses.map((course) => (
                    <div
                      key={course.courseId}
                      className="flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200"
                    >
                      {/* Course Name */}
                      <button className="flex-1 text-left hover:text-teal-600">
                        <span className="font-medium text-gray-900">{course.courseName}</span>
                        <span className="text-sm text-gray-500 ml-2">({course.teacherName})</span>
                      </button>

                      {/* Homework Grade */}
                      <div className="flex items-center gap-2 w-24">
                        <div className={`w-2 h-2 rounded-full ${getGradeIndicatorColor(course.homeworkGrade)}`} />
                        <span className="text-sm text-gray-600">HW: {course.homeworkGrade}</span>
                      </div>

                      {/* Exam Grade */}
                      <div className="flex items-center gap-2 w-24">
                        <div className={`w-2 h-2 rounded-full ${getGradeIndicatorColor(course.examGrade)}`} />
                        <span className="text-sm text-gray-600">Exam: {course.examGrade}</span>
                      </div>

                      {/* Final Grade */}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getGradeBadgeColor(course.finalGrade)}`}>
                        Final: {course.finalGrade}
                      </span>

                      {/* Submission Progress */}
                      <div className="text-sm text-gray-600 w-16">
                        {course.submissionProgress.completed}/{course.submissionProgress.total}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {filteredStudents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No students found</p>
        </div>
      )}
    </div>
  );
}

interface CourseGradesViewProps {
  courses: GradeCourse[];
  searchQuery: string;
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  getGradeBadgeColor: (grade: number) => string;
  getGradeIndicatorColor: (grade: number) => string;
}

function CourseGradesView({
  courses,
  searchQuery,
  expandedId,
  onToggleExpand,
  getGradeBadgeColor,
  getGradeIndicatorColor,
}: CourseGradesViewProps) {
  const filteredCourses = courses.filter((course) =>
    course.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-3">
      {filteredCourses.map((course) => (
        <div
          key={course.id}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          {/* Course Header Row */}
          <div
            className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => onToggleExpand(course.id)}
          >
            {/* Teacher Avatar */}
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-sm font-medium text-teal-700">
              {course.teacher.name.split(' ').map(n => n[0]).join('')}
            </div>

            {/* Course Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">{course.name}</h3>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  course.type === 'async' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {course.type === 'async' ? 'Async' : 'Sync'}
                </span>
              </div>
              <p className="text-sm text-gray-500">{course.cohort}</p>
            </div>

            {/* Stats */}
            <div className="hidden md:flex items-center gap-6 text-sm">
              <div className="text-gray-600">
                <span className="font-medium">{course.studentCount}</span> Students
              </div>
              <div className="text-gray-600">
                <span className="font-medium">{course.attendanceSummary}%</span> Attendance
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getGradeBadgeColor(course.averageGrade)}`}>
                Avg: {course.averageGrade}
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="px-3 py-1 text-sm text-teal-600 hover:bg-teal-50 rounded-lg flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" />
                View Course
              </button>

              {/* Expand/Collapse Icon */}
              {expandedId === course.id ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* Expanded Student Grades */}
          {expandedId === course.id && (
            <div className="border-t border-gray-200 bg-gray-50">
              <div className="p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Student Grades</h4>
                {course.students.length > 0 ? (
                  <div className="space-y-2">
                    {course.students.map((student) => (
                      <div
                        key={student.id}
                        className="flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200"
                      >
                        {/* Student Avatar */}
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                          {student.name.split(' ').map(n => n[0]).join('')}
                        </div>

                        {/* Student Name */}
                        <button className="flex-1 text-left hover:text-teal-600">
                          <span className="font-medium text-gray-900">{student.name}</span>
                        </button>

                        {/* Homework Grade */}
                        <div className="flex items-center gap-2 w-24">
                          <div className={`w-2 h-2 rounded-full ${getGradeIndicatorColor(student.homeworkGrade)}`} />
                          <span className="text-sm text-gray-600">HW: {student.homeworkGrade}</span>
                        </div>

                        {/* Exam Grade */}
                        <div className="flex items-center gap-2 w-24">
                          <div className={`w-2 h-2 rounded-full ${getGradeIndicatorColor(student.examGrade)}`} />
                          <span className="text-sm text-gray-600">Exam: {student.examGrade}</span>
                        </div>

                        {/* Final Grade */}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getGradeBadgeColor(student.finalGrade)}`}>
                          Final: {student.finalGrade}
                        </span>

                        {/* Edit Action */}
                        <button
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Edit grade"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No student grades available</p>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {filteredCourses.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No courses found</p>
        </div>
      )}
    </div>
  );
}
